import { get, post } from "aws-amplify/api";
import Moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { Alert, Button, Col, Modal, Row, Table } from "react-bootstrap";
import { PacmanLoader } from "react-spinners";
import { useLocation, useParams } from "react-router-dom";
import 'containers/common/Detail.css';
import SaleSheetFollowUpCard from "containers/queues/SaleSheetFollowUpCard";
import { PAGE_SIZES } from "utils/pageSizeConfig";

function SaleSheetFollowUpDetailQueue({ isAuthenticated }) {
    const location = useLocation();
    const params = useParams();

    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [showAlert, setShowAlert] = useState(false);
    const [saleSheets, setSaleSheets] = useState([]);
    const [saleStatusOptions, setSaleStatusOptions] = useState([]);
    const [pageIndex, setPageIndex] = useState(0);

    const pageSize = PAGE_SIZES.saleSheetFollowUp;

    const handleError = useCallback((error) => {
        setErrorMessage(String(error));
        setShowAlert(true);
    }, []);

    const getCityscopeOptions = useCallback(async () => {
        const response = await get({
            apiName: "colin",
            path: "/cityscopes/dropdown",
        }).response;
        const payload = await response.body.json();
        return payload || [];
    }, []);

    const getAllCityscopeCsv = useCallback(async () => {
        const options = await getCityscopeOptions();
        return options
            .map((option) => option.value ?? option.cityscope ?? option.label)
            .filter(Boolean)
            .join(",");
    }, [getCityscopeOptions]);

    const getPageData = useCallback(async (filter) => {
        const response = await post({
            apiName: "colin",
            path: "/queues/salesheets/all",
            options: {
                body: filter,
            },
        }).response;
        return response.body.json();
    }, []);

    const getSaleStatusOptions = useCallback(async () => {
        const response = await get({
            apiName: "colin",
            path: "/salestatus",
        }).response;
        const payload = await response.body.json();

        if (!Array.isArray(payload)) {
            return [];
        }

        return payload
            .map((item) => {
                const value = item?.value ?? item?.sale_status_code ?? item?.id ?? "";
                const label = item?.label ?? item?.sale_status ?? item?.name ?? "";
                return {
                    value: value === null || value === undefined ? "" : String(value),
                    label: label || String(value || ""),
                };
            })
            .filter((option) => option.value !== "" || option.label !== "");
    }, []);

    const normaliseAgentIds = useCallback((ids) => {
        if (!Array.isArray(ids) || ids.length === 0) {
            return [0];
        }

        return ids.map((id) => (id === null ? 0 : id));
    }, []);

    const normaliseFollowUpDate = useCallback((value) => {
        if (!value) {
            return Moment().format();
        }

        const parsed = Moment(value);
        return parsed.isValid() ? parsed.format() : Moment().format();
    }, []);

    const extractSaleSheets = useCallback((payload) => {
        if (Array.isArray(payload)) {
            if (payload[0]?.data?.salesheet_list?.sale_sheets) {
                return payload[0].data.salesheet_list.sale_sheets;
            }

            return payload;
        }

        return payload?.data?.salesheet_list?.sale_sheets || [];
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        (async () => {
            try {
                setIsLoading(true);

                const state = location.state || {};
                const routeAgentId = Number(params.id);
                const hasRouteAgentId = Number.isFinite(routeAgentId) && routeAgentId > 0;
                const stateFollowUpDate = normaliseFollowUpDate(state.followupdate);
                const hasStateAgentIds = Array.isArray(state.agentids) && state.agentids.length > 0;
                const stateAgentIds = hasStateAgentIds ? normaliseAgentIds(state.agentids) : [];
                const effectiveAgentIds = hasStateAgentIds
                    ? stateAgentIds
                    : hasRouteAgentId
                        ? [routeAgentId]
                        : normaliseAgentIds([]);

                // Request rule: when no cityscope selection exists, send all cityscope values.
                const resolvedCityscopeCsv =
                    typeof state.cityscope === "string" && state.cityscope.trim().length > 0
                        ? state.cityscope
                        : await getAllCityscopeCsv();

                const requestBody = {
                    agentids: effectiveAgentIds,
                    cityscope: resolvedCityscopeCsv,
                    followupdate: stateFollowUpDate,
                };

                const pageData = await getPageData(requestBody);
                const localSaleSheets = extractSaleSheets(pageData);
                const localSaleStatusOptions = await getSaleStatusOptions();

                setSaleSheets(localSaleSheets || []);
                setSaleStatusOptions(localSaleStatusOptions);

                document.title = `Sale sheet follow ups: ${resolvedCityscopeCsv || "all"}`;
                setIsLoading(false);
            } catch (error) {
                handleError(error);
                setIsLoading(false);
            }
        })();
    }, [extractSaleSheets, getAllCityscopeCsv, getPageData, getSaleStatusOptions, handleError, isAuthenticated, location.state, normaliseAgentIds, normaliseFollowUpDate, params.id]);

    const totalPages = useMemo(() => {
        if (!saleSheets.length) {
            return 1;
        }

        return Math.ceil(saleSheets.length / pageSize);
    }, [pageSize, saleSheets.length]);

    const [isPrinting, setIsPrinting] = useState(false);

    useEffect(() => {
        const handleBeforePrint = () => {
            flushSync(() => {
                setIsPrinting(true);
            });
        };
        const handleAfterPrint = () => {
            setIsPrinting(false);
        };

        window.addEventListener("beforeprint", handleBeforePrint);
        window.addEventListener("afterprint", handleAfterPrint);

        return () => {
            window.removeEventListener("beforeprint", handleBeforePrint);
            window.removeEventListener("afterprint", handleAfterPrint);
        };
    }, []);

    const displayedSaleSheets = useMemo(() => {
        if (isPrinting) {
            return saleSheets.map((salesheet, index) => ({
                salesheet,
                rowKey: `print-${salesheet?.sale_sheet_id ?? "no-id"}-${index}`,
            }));
        }

        const start = pageIndex * pageSize;
        const end = start + pageSize;

        return saleSheets.slice(start, end).map((salesheet, index) => ({
            salesheet,
            rowKey: `${salesheet?.sale_sheet_id ?? "no-id"}-${start + index}`,
        }));
    }, [isPrinting, pageIndex, pageSize, saleSheets]);

    useEffect(() => {
        setPageIndex(0);
    }, [saleSheets]);

    useEffect(() => {
        if (pageIndex > totalPages - 1) {
            setPageIndex(Math.max(totalPages - 1, 0));
        }
    }, [pageIndex, totalPages]);

    function renderComponent() {
        return (
            <div className="componentRendering">
                <Modal show={showAlert} onHide={() => setShowAlert(false)} centered>
                    <Modal.Header closeButton> <Modal.Title>Uh Oh!</Modal.Title></Modal.Header>
                    <Modal.Body><Alert variant="danger">{errorMessage}</Alert></Modal.Body>
                </Modal>
                {/* <Row>
                    <Col>
                        <small>
                            Scope: {cityscopeCsv || "all"} | Follow up: {followupdate || "n/a"} | Selection: {selectedSummary}
                        </small>
                    </Col>
                </Row> */}
                <Row className="show-Container">
                    <Col sm={12} md={12} lg={12}>
                        {isLoading ? (
                            <div className="outer-div">
                                <div className="sweet-loading inner-div">
                                    <PacmanLoader
                                        cssOverride={{ display: "block", margin: "0 auto" }}
                                        size={20}
                                        color={"#e03c31"}
                                        loading
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="salesheets">
                                {saleSheets.length > pageSize && (
                                    <Row className="mb-2 align-items-center d-print-none">
                                        <Col>
                                            <small>
                                                Showing {pageIndex * pageSize + 1}-{Math.min((pageIndex + 1) * pageSize, saleSheets.length)} of {saleSheets.length}
                                            </small>
                                        </Col>
                                        <Col className="text-end">
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
                                                disabled={pageIndex === 0}
                                            >
                                                Previous
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setPageIndex((prev) => Math.min(prev + 1, totalPages - 1))}
                                                disabled={pageIndex >= totalPages - 1}
                                            >
                                                Next
                                            </Button>
                                        </Col>
                                    </Row>
                                )}
                                {displayedSaleSheets.map(({ salesheet, rowKey }, index) => (
                                    <React.Fragment key={rowKey}>
                                        <SaleSheetFollowUpCard
                                            salesheet={salesheet}
                                            saleStatusOptions={saleStatusOptions}
                                        />
                                        {index < displayedSaleSheets.length - 1 && <div className="page-break" />}
                                    </React.Fragment>
                                ))}
                                {saleSheets.length === 0 && (
                                    <Table bordered hover size="sm" responsive>
                                        <thead>
                                            <tr>
                                                <th>Salesheets</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>No data to display</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                )}
                            </div>
                        )}
                    </Col>
                </Row>
            </div>
        );
    }
    return (
        <div className="SaleSheetFollowUpDetailQueue">
            {isAuthenticated ? renderComponent() : null}
        </div>
    );
}

export default SaleSheetFollowUpDetailQueue;